var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { differenceInDays, differenceInHours, differenceInMinutes, differenceInMonths, differenceInWeeks, differenceInYears, format, isPast, } from "date-fns";
import { SHLLinkStatus } from "healthex-common/shl.dtos";
import { computed, observable } from "mobx";
import { Disposer } from "../Infra/Disposer";
import { NOT_LOADED } from "../Infra/RemoteData";
import { MobileBottomNavigationButtonType } from "./PatientResearchDashboardUIManager";
import { getObservableT } from "../Infra/i18next";
import { i18nGetDateFnsLocale } from "../Infra/i18next";
import { resourceScopeDescription } from "./DataScopeDescriptions";
import { SHLUIManager } from "../Logic/SHLUIManager";
import { SharingSection } from "./PatientStudiesListViewModel";
const t = getObservableT("patients");
export class PatientSharingLinkDetailsViewModel {
    constructor(props) {
        this.disposer = new Disposer();
        this.loadRequestId = 0;
        this.shl = NOT_LOADED;
        this.accessLog = NOT_LOADED;
        this.loadError = null;
        this.revokeInProgress = false;
        this.revokeError = null;
        this.container = props.container;
        this.shlService = props.container.shlService;
        this.authManager = props.container.authManager;
        this.routingUIManager = props.container.routingUIManager;
        this.invalidationManager = props.container.invalidationManager;
    }
    get shlId() {
        var _a, _b;
        return (_b = (_a = this.routingUIManager.currentRouteParams) === null || _a === void 0 ? void 0 : _a.shlId) !== null && _b !== void 0 ? _b : null;
    }
    get patientId() {
        var _a;
        return (_a = this.authManager.id) !== null && _a !== void 0 ? _a : null;
    }
    get isLoading() {
        return this.shl === NOT_LOADED || this.accessLog === NOT_LOADED;
    }
    get isInactive() {
        if (this.shl === NOT_LOADED || this.shl === null)
            return true;
        const shl = this.shl;
        return shl.status !== SHLLinkStatus.ACTIVE || isPast(new Date(shl.expiresAt));
    }
    get canRevoke() {
        return !this.isInactive;
    }
    run() {
        this.disposer.reaction(() => [this.shlId, this.patientId], ([shlId, patientId]) => {
            void this.load(shlId, patientId);
        }, { name: "loadSHLAndAccessLog", fireImmediately: true });
    }
    stop() {
        this.disposer.dispose();
    }
    async load(shlId, patientId) {
        const requestId = ++this.loadRequestId;
        if (!shlId || !patientId) {
            this.shl = null;
            this.accessLog = [];
            this.loadError = null;
            return;
        }
        this.shl = NOT_LOADED;
        this.accessLog = NOT_LOADED;
        this.loadError = null;
        const [shlResult, accessLogResult] = await Promise.allSettled([
            this.shlService.getSHLById(patientId, shlId),
            this.shlService.getSHLAccessLog(patientId, shlId),
        ]);
        if (requestId !== this.loadRequestId)
            return;
        if (shlResult.status === "fulfilled") {
            this.shl = shlResult.value;
        }
        else {
            this.shl = null;
            this.loadError = t("Could not load share link");
        }
        if (accessLogResult.status === "fulfilled") {
            this.accessLog = accessLogResult.value;
        }
        else {
            this.accessLog = [];
        }
    }
    formatDate(date) {
        return format(new Date(date), "MM/dd/yyyy", { locale: i18nGetDateFnsLocale() });
    }
    formatAccessLogDate(date) {
        const d = new Date(date);
        const now = new Date();
        const minutes = differenceInMinutes(now, d);
        const hours = differenceInHours(now, d);
        const days = differenceInDays(now, d);
        const weeks = differenceInWeeks(now, d);
        const months = differenceInMonths(now, d);
        const years = differenceInYears(now, d);
        if (minutes < 1)
            return t("Just now");
        if (minutes < 60)
            return minutes === 1 ? t("1 minute ago") : t("{{count}} minutes ago", { count: minutes });
        if (hours < 24)
            return hours === 1 ? t("1 hour ago") : t("{{count}} hours ago", { count: hours });
        if (days < 7)
            return days === 1 ? t("1 day ago") : t("{{count}} days ago", { count: days });
        if (days < 30)
            return weeks === 1 ? t("1 week ago") : t("{{count}} weeks ago", { count: weeks });
        if (months < 12)
            return months === 1 ? t("1 month ago") : t("{{count}} months ago", { count: months });
        return years === 1 ? t("1 year ago") : t("{{count}} years ago", { count: years });
    }
    getLinkTitle() {
        var _a;
        if (this.shl === NOT_LOADED || this.shl === null)
            return "";
        return (_a = this.shl.label) !== null && _a !== void 0 ? _a : "";
    }
    getLinkTypeText() {
        if (this.shl === NOT_LOADED || this.shl === null)
            return "";
        return this.shl.isOneTimeUse ? t("One-time use") : t("30-day access");
    }
    getCreatedOnDate() {
        if (this.shl === NOT_LOADED || this.shl === null)
            return "";
        return this.formatDate(this.shl.createdAt);
    }
    getExpiresDate() {
        if (this.shl === NOT_LOADED || this.shl === null)
            return "";
        return this.formatDate(this.shl.expiresAt);
    }
    getStatusReasonRow() {
        if (this.shl === NOT_LOADED || this.shl === null)
            return null;
        if (!this.isInactive)
            return null;
        const shl = this.shl;
        if (shl.status === SHLLinkStatus.USED) {
            const date = shl.usedAt ? this.formatDate(shl.usedAt) : "";
            return { label: t("Used on"), date };
        }
        if (shl.status === SHLLinkStatus.REVOKED) {
            const date = shl.revokedAt ? this.formatDate(shl.revokedAt) : "";
            return { label: t("Deactivated by you"), date };
        }
        return { label: t("Expired on"), date: this.formatDate(shl.expiresAt) };
    }
    getWhatsIncludedText() {
        if (this.shl === NOT_LOADED || this.shl === null)
            return "";
        const shl = this.shl;
        if (shl.fullRecordWithoutRestrictedData)
            return t("All health records");
        const scopes = SHLUIManager.removeImpliedScopes(shl.consentDataResourceScopes);
        if (scopes.length === 0)
            return "";
        return scopes
            .map((scope, idx) => {
            const desc = resourceScopeDescription(scope, true);
            if (idx === 0) {
                return desc.charAt(0).toUpperCase() + desc.slice(1);
            }
            else {
                return desc.toLowerCase();
            }
        })
            .join(", ");
    }
    async copyLink(link) {
        try {
            await navigator.clipboard.writeText(link);
            this.container.bannerUIManager.displayBanner({
                text: t("Link copied"),
                type: "SUCCESS",
                durationInMS: 3000,
            });
        }
        catch {
            this.container.bannerUIManager.displayBanner({
                text: t("Could not copy link. Please try again."),
                type: "ERROR",
                durationInMS: 3000,
            });
        }
    }
    goBackToSharing() {
        this.container.patientResearchDashboardUIManager.goToPage(MobileBottomNavigationButtonType.RESEARCH, SharingSection.SHARED_LINKS);
    }
    async revokeSHL() {
        const patientId = this.patientId;
        const shlId = this.shlId;
        if (!patientId || !shlId || !this.canRevoke || this.revokeInProgress)
            return;
        this.revokeInProgress = true;
        this.revokeError = null;
        try {
            const updated = await this.shlService.revokeSHL(patientId, shlId);
            this.shl = updated;
            this.invalidationManager.invalidate();
            this.container.bannerUIManager.displayBanner({
                text: t("Link deactivated"),
                type: "SUCCESS",
                durationInMS: 3000,
            });
        }
        catch {
            this.revokeError = t("Something went wrong, please try again later.");
        }
        finally {
            this.revokeInProgress = false;
        }
    }
}
__decorate([
    observable
], PatientSharingLinkDetailsViewModel.prototype, "shl", void 0);
__decorate([
    observable
], PatientSharingLinkDetailsViewModel.prototype, "accessLog", void 0);
__decorate([
    observable
], PatientSharingLinkDetailsViewModel.prototype, "loadError", void 0);
__decorate([
    observable
], PatientSharingLinkDetailsViewModel.prototype, "revokeInProgress", void 0);
__decorate([
    observable
], PatientSharingLinkDetailsViewModel.prototype, "revokeError", void 0);
__decorate([
    computed
], PatientSharingLinkDetailsViewModel.prototype, "shlId", null);
__decorate([
    computed
], PatientSharingLinkDetailsViewModel.prototype, "patientId", null);
__decorate([
    computed
], PatientSharingLinkDetailsViewModel.prototype, "isLoading", null);
__decorate([
    computed
], PatientSharingLinkDetailsViewModel.prototype, "isInactive", null);
__decorate([
    computed
], PatientSharingLinkDetailsViewModel.prototype, "canRevoke", null);
